import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ttApi } from "../../lib/ttApi";
import {
  ArrowLeft, Plus, Trash2, Loader2, CheckCircle2, XCircle,
  AlertTriangle, Clock, Sparkles, Brain, Target, Shield, Zap, Users,
} from "lucide-react";

// ─── Shared primitives (self-contained) ──────────────────────────────────
const Tile = ({ label, value, sub, tone }) => {
  const toneClass = { green: "text-emerald-700", amber: "text-amber-700", red: "text-red-700" }[tone] || "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold tracking-tight mt-1 ${toneClass}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
};
const L = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    {children}
  </label>
);
const F = ({ label, v, onChange, testid, type = "text" }) => (
  <L label={label}>
    <input type={type} value={v ?? ""} onChange={e => onChange(e.target.value)} className="fld py-2 px-3 w-full" data-testid={testid}/>
  </L>
);
const Err = ({ children }) => <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{children}</div>;
const SmallModal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)" }} onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-md shadow-2xl max-w-lg w-full p-5 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="font-display text-lg font-extrabold tracking-tight mb-3">{title}</div>
      {children}
    </div>
  </div>
);
const Actions = ({ onClose, busy, testid, label = "Save" }) => (
  <div className="flex items-center justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
    <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid={testid}>{busy ? "Saving…" : label}</button>
  </div>
);
const BackLink = () => <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={13}/> Control Tower</Link>;

// ─── Go-Live Scorecard ───────────────────────────────────────────────────
const GL_CATEGORIES = ["Technical", "Business", "Operational", "Legal/Compliance", "Executive"];
const GL_STATUSES = ["Pending", "Conditional GO", "GO", "NO-GO"];

function statusPill(s) {
  const map = {
    "GO":             ["pill-green", CheckCircle2],
    "Conditional GO": ["bg-amber-50 text-amber-800 border border-amber-200", AlertTriangle],
    "NO-GO":          ["bg-red-50 text-red-700 border border-red-200", XCircle],
    "Pending":        ["pill-neutral", Clock],
  };
  const [cls, Icon] = map[s] || map["Pending"];
  return <span className={`pill ${cls} inline-flex items-center gap-1 text-[10px]`}><Icon size={11}/> {s}</span>;
}

function decisionBanner(d) {
  const map = {
    "GO":             ["bg-emerald-50 border-emerald-400 text-emerald-900", CheckCircle2],
    "Conditional GO": ["bg-amber-50 border-amber-400 text-amber-900", AlertTriangle],
    "NO-GO":          ["bg-red-50 border-red-500 text-red-900", XCircle],
    "Not Ready":      ["bg-zinc-50 border-zinc-400 text-zinc-800", Clock],
  };
  const [cls, Icon] = map[d.decision] || map["Not Ready"];
  return (
    <div className={`border-l-4 rounded-sm p-4 flex items-start gap-3 ${cls}`} data-testid="tt-golive-decision">
      <Icon size={28} className="shrink-0 mt-1"/>
      <div className="flex-1">
        <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">Overall decision</div>
        <div className="font-display text-2xl font-extrabold tracking-tight">{d.decision}</div>
        <div className="text-sm mt-1">{d.rationale}</div>
        {d.pct_go > 0 && <div className="text-xs font-mono mt-1 opacity-80">{d.pct_go}% of items at GO · {d.totals.all} total sign-off(s)</div>}
      </div>
    </div>
  );
}

export function GoLiveCenter() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [decision, setDecision] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(true);
  const refresh = async () => {
    setBusy(true);
    try {
      const [r, d] = await Promise.all([ttApi.listGoLive(pid), ttApi.goLiveDecision(pid)]);
      setRows(r.items || []); setDecision(d);
    } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (gid) => { if (window.confirm("Delete sign-off item?")) { await ttApi.deleteGoLive(pid, gid); refresh(); } };

  // Group by category
  const grouped = GL_CATEGORIES.map(cat => ({ cat, items: rows.filter(r => r.category === cat) })).filter(g => g.items.length > 0);

  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-golive">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><Shield size={11}/> Go-Live Governance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Go-Live Scorecard</h1>
          <p className="text-sm text-zinc-600 mt-1">Named-executive sign-offs across all categories · conditional GO / NO-GO gates</p>
        </div>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-golive"><Plus size={13}/> New sign-off item</button>
      </div>

      {decision && (
        <div className="mb-6">
          {decisionBanner(decision)}
          {decision.conditions?.length > 0 && (
            <div className="mt-3 bg-white border border-amber-200 rounded-md p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800 mb-2">Aggregated conditions ({decision.conditions.length})</div>
              <ul className="space-y-1 text-sm">
                {decision.conditions.map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-700 mt-0.5">•</span>
                    <span><strong>{c.owner}</strong> on <em>{c.item}</em>: {c.condition}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {decision.executive_pending?.length > 0 && (
            <div className="mt-3 bg-white border border-red-200 rounded-md p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-red-800 mb-2">Executive sign-offs still pending</div>
              <div className="text-sm text-zinc-700">{decision.executive_pending.join(" · ")}</div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Tile label="GO" value={decision?.totals?.GO || 0} sub="signed" tone="green"/>
        <Tile label="Conditional" value={decision?.totals?.["Conditional GO"] || 0} sub="with conditions" tone={(decision?.totals?.["Conditional GO"] || 0) > 0 ? "amber" : "green"}/>
        <Tile label="NO-GO" value={decision?.totals?.["NO-GO"] || 0} sub="blockers" tone={(decision?.totals?.["NO-GO"] || 0) > 0 ? "red" : "green"}/>
        <Tile label="Pending" value={decision?.totals?.Pending || 0} sub="awaiting" tone={(decision?.totals?.Pending || 0) > 0 ? "amber" : "green"}/>
        <Tile label="All items" value={decision?.totals?.all || 0} sub="total"/>
      </div>

      {busy ? (
        <div className="text-sm text-zinc-500"><Loader2 className="inline animate-spin" size={13}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 bg-white border border-dashed rounded-sm text-center text-sm text-zinc-500">
          No sign-off items defined yet. Click <b>New sign-off item</b> to build the scorecard — start with "All P1 defects resolved" (Technical), "Business cutover training complete" (Business), "CIO sign-off" (Executive).
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(g => (
            <div key={g.cat} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`tt-golive-cat-${g.cat}`}>
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                {g.cat} ({g.items.length})
              </div>
              <table className="w-full text-sm">
                <thead className="bg-white text-xs font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                  <tr>
                    <th className="text-left px-4 py-2">Item</th>
                    <th className="text-left px-4 py-2">Owner</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Signed</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map(r => (
                    <tr key={r._id} className="border-b border-zinc-50 last:border-0 align-top" data-testid={`tt-golive-${r._id}`}>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-zinc-900">{r.item}</div>
                        {r.signoff_notes && <div className="text-[11px] text-zinc-600 mt-1">{r.signoff_notes}</div>}
                        {r.conditions?.length > 0 && (
                          <div className="text-[10px] text-amber-700 mt-1">
                            Conditions: {r.conditions.join(" · ")}
                          </div>
                        )}
                        {r.evidence && <div className="text-[10px] text-zinc-500 mt-1 font-mono">{r.evidence}</div>}
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-xs font-semibold">{r.owner_name || "—"}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{r.owner_role || ""}</div>
                      </td>
                      <td className="px-4 py-2">{statusPill(r.signoff_status)}</td>
                      <td className="px-4 py-2 text-[11px] text-zinc-500 font-mono">
                        {r.signoff_at ? (
                          <>
                            <div>{new Date(r.signoff_at).toLocaleDateString()}</div>
                            <div>by {r.signoff_by || "—"}</div>
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setOpen({ item: r })} className="text-xs text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid={`tt-golive-edit-${r._id}`}>Edit</button>
                        <button onClick={() => del(r._id)} className="ml-3 text-xs text-red-700"><Trash2 size={11}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {open && <GoLiveModal pid={pid} initial={open.item} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function GoLiveModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    category: initial?.category || "Technical",
    item: initial?.item || "",
    owner_name: initial?.owner_name || "",
    owner_role: initial?.owner_role || "",
    signoff_status: initial?.signoff_status || "Pending",
    signoff_notes: initial?.signoff_notes || "",
    conditions: (initial?.conditions || []).join("\n"),
    evidence: initial?.evidence || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = {
        ...f,
        conditions: f.conditions.split("\n").map(s => s.trim()).filter(Boolean),
      };
      if (initial?._id) await ttApi.updateGoLive(pid, initial._id, body);
      else await ttApi.createGoLive(pid, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Sign-off" : "New Sign-off Item"} onClose={onClose} testid="tt-golive-modal">
      <form onSubmit={submit} className="space-y-3">
        <L label="Category">
          <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-golive-category">
            {GL_CATEGORIES.map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Item" v={f.item} onChange={v => setF({...f, item: v})} testid="tt-golive-item"/>
        <div className="grid grid-cols-2 gap-3">
          <F label="Owner name" v={f.owner_name} onChange={v => setF({...f, owner_name: v})} testid="tt-golive-owner"/>
          <F label="Owner role (CIO, CFO, …)" v={f.owner_role} onChange={v => setF({...f, owner_role: v})} testid="tt-golive-role"/>
        </div>
        <L label="Sign-off status">
          <select value={f.signoff_status} onChange={e => setF({...f, signoff_status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-golive-status">
            {GL_STATUSES.map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Sign-off notes" v={f.signoff_notes} onChange={v => setF({...f, signoff_notes: v})} testid="tt-golive-notes"/>
        <L label="Conditions (one per line — only applies to Conditional GO)">
          <textarea value={f.conditions} onChange={e => setF({...f, conditions: e.target.value})} className="fld py-2 px-3 w-full" rows={3} data-testid="tt-golive-conditions"/>
        </L>
        <F label="Evidence (ticket IDs, approval URLs)" v={f.evidence} onChange={v => setF({...f, evidence: v})} testid="tt-golive-evidence"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-golive-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── AI Risk Agents Dashboard ────────────────────────────────────────────
const AGENTS = [
  { id: "risk",      label: "Go-Live Risk Agent", icon: Target, call: "aiGoLiveRisk", color: "#4A0F1A" },
  { id: "coverage",  label: "Test Coverage Agent", icon: Zap,    call: "aiTestCoverage", color: "#1E3A8A" },
  { id: "defects",   label: "Defect Intelligence", icon: Brain,  call: "aiDefectIntel", color: "#065F46" },
];

export function AIAgentsCenter() {
  const { pid } = useParams();
  const [results, setResults] = useState({});   // { agentId: payload }
  const [busy, setBusy] = useState({});         // { agentId: bool }

  const run = async (agent) => {
    setBusy(b => ({ ...b, [agent.id]: true }));
    try {
      const r = await ttApi[agent.call](pid);
      setResults(x => ({ ...x, [agent.id]: r }));
    } catch (e) {
      setResults(x => ({ ...x, [agent.id]: { error: e.message } }));
    } finally {
      setBusy(b => ({ ...b, [agent.id]: false }));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-ai">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><Sparkles size={11}/> NAVBOT · Claude Sonnet 4.5</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">AI Risk Agents</h1>
          <p className="text-sm text-zinc-600 mt-1">On-demand risk, coverage, and defect analysis across the entire control tower.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {AGENTS.map(a => {
          const Icon = a.icon;
          const r = results[a.id];
          const b = busy[a.id];
          return (
            <div key={a.id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`tt-ai-${a.id}`}>
              <div className="p-4 border-b border-zinc-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm grid place-items-center text-white shrink-0" style={{ background: a.color }}>
                  <Icon size={18}/>
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-extrabold tracking-tight">{a.label}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                    {r?.ai_available === false ? "LLM offline — deterministic only" : "AI + deterministic analysis"}
                  </div>
                </div>
              </div>
              <div className="p-4 min-h-[180px]">
                {!r && (
                  <button onClick={() => run(a)} disabled={b} className="btn btn-primary btn-sm w-full inline-flex items-center justify-center gap-2" data-testid={`tt-ai-run-${a.id}`}>
                    {b ? <><Loader2 className="animate-spin" size={13}/> Analyzing…</> : <><Sparkles size={13}/> Run analysis</>}
                  </button>
                )}
                {r?.error && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-xs text-red-800">{r.error}</div>}
                {r && !r.error && (
                  <>
                    {a.id === "risk" && <RiskSummary r={r}/>}
                    {a.id === "coverage" && <CoverageSummary r={r}/>}
                    {a.id === "defects" && <DefectSummary r={r}/>}
                    <button onClick={() => run(a)} disabled={b} className="btn btn-ghost btn-sm w-full mt-3 inline-flex items-center justify-center gap-1 text-xs" data-testid={`tt-ai-rerun-${a.id}`}>
                      {b ? <Loader2 className="animate-spin" size={12}/> : <Sparkles size={12}/>} Re-run
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}

function AiBriefing({ text }) {
  if (!text) return <div className="text-[11px] text-zinc-400 italic mt-2">AI briefing offline — review deterministic metrics above.</div>;
  return (
    <div className="mt-3 p-3 bg-zinc-50 border-l-2 border-zinc-800 rounded-sm">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">NAVBOT briefing</div>
      <div className="text-xs text-zinc-800 whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
}

function RiskSummary({ r }) {
  const bandColor = { LOW: "text-emerald-700", MEDIUM: "text-amber-700", HIGH: "text-orange-700", CRITICAL: "text-red-700" }[r.risk.band] || "text-zinc-900";
  return (
    <>
      <div className="flex items-baseline gap-3">
        <div className={`font-display text-4xl font-extrabold tracking-tight ${bandColor}`}>{r.risk.score}</div>
        <div className={`font-mono text-xs uppercase tracking-widest ${bandColor}`}>{r.risk.band} risk</div>
      </div>
      <ul className="mt-2 space-y-0.5 text-[11px] text-zinc-600">
        {r.risk.reasons.slice(0, 4).map((x, i) => <li key={i}>• {x}</li>)}
        {r.risk.reasons.length === 0 && <li className="text-emerald-700">• No material risk factors detected</li>}
      </ul>
      <AiBriefing text={r.ai_briefing}/>
    </>
  );
}

function CoverageSummary({ r }) {
  const m = r.metrics;
  const tone = m.coverage_pct >= 95 ? "text-emerald-700" : m.coverage_pct >= 80 ? "text-amber-700" : "text-red-700";
  return (
    <>
      <div className="flex items-baseline gap-3">
        <div className={`font-display text-4xl font-extrabold tracking-tight ${tone}`}>{m.coverage_pct}%</div>
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">coverage</div>
      </div>
      <div className="text-[11px] text-zinc-600 mt-2">
        {m.requirements_uncovered} of {m.requirements_total} reqs uncovered · {m.requirements_high_prio_uncovered} high/critical · {m.thin_scenarios_count} thin scenarios
      </div>
      {r.high_priority_gaps.length > 0 && (
        <div className="mt-2 text-[11px]">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Top gaps</div>
          {r.high_priority_gaps.slice(0, 3).map(g => <div key={g.code} className="text-zinc-700 truncate">· {g.code}: {g.title}</div>)}
        </div>
      )}
      <AiBriefing text={r.ai_briefing}/>
    </>
  );
}

function DefectSummary({ r }) {
  return (
    <>
      <div className="flex items-baseline gap-3">
        <div className="font-display text-4xl font-extrabold tracking-tight text-zinc-900">{r.totals.open}</div>
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">open · {r.totals.all} total</div>
      </div>
      <div className="text-[11px] text-zinc-600 mt-2">
        Critical {r.totals.by_severity.Critical || 0} · High {r.totals.by_severity.High || 0} · Medium {r.totals.by_severity.Medium || 0}
      </div>
      {r.hotspots.length > 0 && (
        <div className="mt-2 text-[11px]">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Hotspots</div>
          {r.hotspots.slice(0, 3).map((h, i) => <div key={i} className="text-zinc-700">· {h.process} ({h.defects})</div>)}
        </div>
      )}
      {r.aged_open.length > 0 && (
        <div className="mt-2 text-[11px]">
          <div className="font-mono text-[9px] uppercase tracking-widest text-red-700">Aged open</div>
          {r.aged_open.slice(0, 2).map(a => <div key={a.id} className="text-red-700 truncate">· {a.title} ({a.age_days}d)</div>)}
        </div>
      )}
      <AiBriefing text={r.ai_briefing}/>
    </>
  );
}
